import React, { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "../../styles/view-request-detail.css";
import UserDetail from "./reusable/user-detail-request-item";
import RequestDetailItem from "./reusable/request-detail-item";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import {
  convertTo12HourPlusDateFormat,
  formatToPeso,
} from "../../util/helperFunctions";

function RequestDetail({
  handleShowApproversModal,
  setApproversData,
  userDetails,
}) {
  const linkData = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [requisitionData, setRequisitionData] = useState(null);
  const [isUserApprover, setUserApprover] = useState(false);
  const [returnUrl, setReturnUrl] = useState("/");
  const [forTagging, setForTagging] = useState(false);

  const [unauthorized, setUnauthorized] = useState(false);

  const handleApproveDenyRequest = async (status) => {
    const apiResponse = await ApiService.forms.approveDenyRequest(
      requisitionData?.requisition?.form_id,
      requisitionData?.requisition?.form_request_detail?.id,
      status,
    );
    const { success } = apiResponse;

    if (!success) return showSwal(common.errors.APPROVE_REQUEST_ERROR);

    window.location.reload();
  };
  const handleTagRequest = async (status) => {
    const apiResponse = await ApiService.forms.tagRequest(
      requisitionData?.requisition?.form_id,
      requisitionData?.requisition?.form_request_detail?.id,
      status,
    );
    const { success } = apiResponse;

    if (!success) return showSwal(common.errors.TAG_REQUEST_ERROR);

    window.location.href = returnUrl;
  };

  useEffect(() => {
    const getFormRequest = async (requestType, requestLink) => {
      const response = await ApiService.forms.getFormRequestDetail(
        requestType,
        requestLink,
      );
      const { success, request } = response;
      if (!success)
        return showSwal(common.errors.GETTING_REQUESTS_DETAIL_ERROR);

      setRequisitionData(request);
      setApproversData(request?.approval);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    };
    const searchParams = new URLSearchParams(linkData.search);
    const requestType = searchParams.get("t");
    const requestLinkId = searchParams.get("r");
    const _returnUrl = searchParams.get("r_url");
    const tagging = searchParams.get("tg");
    if (
      requestType !== "" &&
      requestType !== null &&
      requestLinkId !== "" &&
      requestLinkId !== null
    ) {
      setReturnUrl(_returnUrl);
      setForTagging(tagging === "true");
      getFormRequest(requestType, requestLinkId);
    } else {
      setReturnUrl("/");
    }
  }, [linkData.search]);

  useEffect(() => {
    if (userDetails && requisitionData) {
      const { approval, overall_approval_status, reject_status } =
        requisitionData;
      const { first_level_approval, second_level_approval } = approval;

      const isFirstLevelApprover = first_level_approval.find(
        (approval) => approval.id === userDetails.id,
      );
      const isSecondLevelApprover = second_level_approval.find(
        (approval) => approval.id === userDetails.id,
      );

      if (isFirstLevelApprover || isSecondLevelApprover) {
        if (
          isFirstLevelApprover &&
          !isFirstLevelApprover.has_voted &&
          !overall_approval_status &&
          !reject_status
        ) {
          setUserApprover(true);
        }
        if (
          isSecondLevelApprover &&
          !isSecondLevelApprover.has_voted &&
          !overall_approval_status &&
          !reject_status
        ) {
          setUserApprover(true);
        }
      }
    }
  }, [userDetails, requisitionData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(linkData.search);
    const requestorUserId = searchParams.get("u");

    if (requestorUserId && userDetails && userDetails.id) {
      const currentUserId = userDetails.id.toString();

      const bypassUsers = ["b38cc479-6c1f-4ede-80df-46d66402be2e"];

      const isBypassUser = bypassUsers.includes(currentUserId);

      if (requestorUserId !== currentUserId && !isBypassUser) {
        console.log(
          "This request is not made by the current user, setting return url to dashboard",
        );
        setUnauthorized(true);
      }
    }
  }, [userDetails]);
  const isRejected = requisitionData?.reject_status;
  const isFullyApproved =
    requisitionData?.overall_approval_status && !isRejected;
  return (
    <>
      {isLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : unauthorized ? (
        <div className="unauthorized-state">
          <div className="unauthorized-card">
            <div className="unauthorized-icon">
              <i className="fa fa-lock" aria-hidden="true"></i>
            </div>

            <span className="unauthorized-eyebrow">Access Restricted</span>
            <h1>Unauthorized Access</h1>
            <p>You do not have permission to view this request.</p>

            <div className="unauthorized-note">
              Please contact the request owner or an administrator if you
              believe this is an error.
            </div>
          </div>
        </div>
      ) : requisitionData ? (
        <div className="view-request-detail-container">
          <a href={returnUrl}>
            <div className="go-back">
              <i className="fa fa-arrow-left fa-3x" aria-hidden="true"></i>
              <span>Go Back</span>
            </div>
          </a>
          <div className="request-detail-row approval-row">
            <div className="request-detail--card approval-master-card">
              <div className="requester-information">
                <div className="approval-information approval-information--enhanced">
                  <div className="approval-title-block">
                    <span className="approval-eyebrow">Approval Summary</span>
                    <h1>Approval Information</h1>
                    <p className="approval-helper-text">
                      {isFullyApproved
                        ? "This request is fully approved."
                        : isRejected
                        ? "This request has been denied."
                        : "Approver details will appear once the request is fully approved."}
                    </p>
                  </div>
                </div>

                <div className="approval-count-grid">
                  <div className="approval-count-box">
                    <span className="approval-count-label">
                      Initial Approval Count Needed
                    </span>
                    <strong className="approval-count-value">
                      {requisitionData?.approval
                        ?.initial_approval_needed_count ?? 0}
                    </strong>
                  </div>

                  <div className="approval-count-box">
                    <span className="approval-count-label">
                      Executive Approval Count Needed
                    </span>
                    <strong className="approval-count-value">
                      {requisitionData?.approval
                        ?.executive_approval_needed_count ?? 0}
                    </strong>
                  </div>
                </div>

                <div className="approval-record-section">
                  <div className="approved-by-preview__header">
                    <div className="approved-by-preview__header-left">
                      <span className="approved-by-preview__eyebrow">
                        Approval Record
                      </span>
                      <p>
                        This section displays the approvers assigned to this
                        request along with their current approval status.
                      </p>
                    </div>

                    <div
                      className={`approved-by-preview__header-badge ${
                        isFullyApproved
                          ? "approved-status"
                          : isRejected
                          ? "denied-status"
                          : "pending-status"
                      }`}
                    >
                      {isFullyApproved ? (
                        <>
                          <i className="fa fa-check-circle"></i>
                          <span>Fully Approved</span>
                        </>
                      ) : isRejected ? (
                        <>
                          <i className="fa fa-times-circle"></i>
                          <span>Rejected</span>
                        </>
                      ) : (
                        <>
                          <i className="fa fa-clock-o"></i>
                          <span>Pending Approval</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="approved-by-preview__grid">
                    <div className="approved-by-preview__section">
                      <div className="approved-by-preview__section-header">
                        <div>
                          <h3>1st Level Approvers</h3>
                          <span className="approved-by-preview__section-subtitle">
                            Initial approval stage
                          </span>
                        </div>
                      </div>

                      <div className="approved-by-preview__list">
                        {requisitionData?.approval?.first_level_approval
                          ?.length > 0 ? (
                          requisitionData?.approval?.first_level_approval.map(
                            (approver, index) => (
                              <div
                                className="approved-by-preview__item"
                                key={index}
                              >
                                <div className="approved-by-preview__item-left">
                                  <div className="approved-by-preview__avatar">
                                    {approver?.display_name
                                      ?.charAt(0)
                                      ?.toUpperCase() || "A"}
                                  </div>

                                  <div className="approved-by-preview__meta">
                                    <span className="approved-by-preview__name">
                                      {approver?.display_name}
                                    </span>
                                  </div>
                                </div>

                                <div className="approved-by-preview__item-right">
                                  {approver?.approved ? (
                                    <span className="approved-by-preview__status">
                                      Approved
                                    </span>
                                  ) : approver?.has_voted ? (
                                    <span className="approved-by-preview__status denied-status">
                                      Rejected
                                    </span>
                                  ) : (
                                    <span className="approved-by-preview__status pending-status">
                                      Pending
                                    </span>
                                  )}

                                  <span className="approved-by-preview__date">
                                    {approver?.date
                                      ? convertTo12HourPlusDateFormat(
                                          approver?.date,
                                        )
                                      : "No date available"}
                                  </span>
                                </div>
                              </div>
                            ),
                          )
                        ) : (
                          <div className="approved-by-preview__empty">
                            No 1st level approvers found.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="approved-by-preview__section">
                      <div className="approved-by-preview__section-header">
                        <div>
                          <h3>2nd Level Approvers</h3>
                          <span className="approved-by-preview__section-subtitle">
                            Executive approval stage
                          </span>
                        </div>
                      </div>

                      <div className="approved-by-preview__list">
                        {requisitionData?.approval?.second_level_approval
                          ?.length > 0 ? (
                          requisitionData?.approval?.second_level_approval.map(
                            (approver, index) => (
                              <div
                                className="approved-by-preview__item"
                                key={index}
                              >
                                <div className="approved-by-preview__item-left">
                                  <div className="approved-by-preview__avatar">
                                    {approver?.display_name
                                      ?.charAt(0)
                                      ?.toUpperCase() || "A"}
                                  </div>

                                  <div className="approved-by-preview__meta">
                                    <span className="approved-by-preview__name">
                                      {approver?.display_name}
                                    </span>
                                  </div>
                                </div>

                                <div className="approved-by-preview__item-right">
                                  {approver?.approved ? (
                                    <span className="approved-by-preview__status">
                                      Approved
                                    </span>
                                  ) : approver?.has_voted ? (
                                    <span className="approved-by-preview__status denied-status">
                                      Rejected
                                    </span>
                                  ) : (
                                    <span className="approved-by-preview__status pending-status">
                                      Pending
                                    </span>
                                  )}

                                  <span className="approved-by-preview__date">
                                    {approver?.date
                                      ? convertTo12HourPlusDateFormat(
                                          approver?.date,
                                        )
                                      : "No date available"}
                                  </span>
                                </div>
                              </div>
                            ),
                          )
                        ) : (
                          <div className="approved-by-preview__empty">
                            No 2nd level approvers found.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="request-detail-row">
            <div className="request-detail--card">
              <div className="requester-information">
                <UserDetail
                  fieldName={"Unique ID: "}
                  fieldValue={
                    requisitionData?.requisition?.form_request_detail
                      ?.reference_id
                  }
                />
                <div className="user-detail">
                  <UserDetail
                    fieldName={"Name: "}
                    fieldValue={`${requisitionData?.requisition?.user_detail?.first_name} ${requisitionData?.requisition?.user_detail?.last_name}`}
                  />
                </div>
                <div className="user-detail">
                  <UserDetail
                    fieldName={"Email Address: "}
                    fieldValue={
                      requisitionData?.requisition?.user_detail?.email_address
                    }
                  />
                </div>
                <div className="user-detail">
                  <UserDetail
                    fieldName={"Company: "}
                    fieldValue={
                      requisitionData?.requisition?.pr_company_detail?.name
                    }
                  />
                </div>
                <div className="user-detail">
                  <UserDetail
                    fieldName={"Date & Time of Request: "}
                    fieldValue={convertTo12HourPlusDateFormat(
                      requisitionData?.requisition?.createdAt,
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="request-detail--card">
              <div className="user-detail">
                <UserDetail
                  fieldName={"Type of Request: "}
                  fieldValue={
                    requisitionData?.requisition?.pr_request_detail?.name
                  }
                />
              </div>
              <div className="user-detail">
                <UserDetail
                  fieldName={"Urgent: "}
                  fieldValue={`${
                    requisitionData?.requisition?.urgent ? "Yes" : "No"
                  }`}
                />
              </div>
              <div className="user-detail">
                <UserDetail
                  fieldName={"Recommended Supplier: "}
                  fieldValue={
                    requisitionData?.requisition?.recommended_supplier
                  }
                />
              </div>
              <div className="user-detail">
                <UserDetail
                  fieldName={"Date Needed: "}
                  fieldValue={convertTo12HourPlusDateFormat(
                    requisitionData?.requisition?.date_needed,
                  )}
                />
              </div>
              <div className="user-detail">
                <UserDetail
                  fieldName={"Total Cost of Request: "}
                  fieldValue={formatToPeso(
                    requisitionData?.requisition?.total_cost,
                  )}
                />
              </div>
            </div>
          </div>

          <div className="request-detail-row">
            <div className="request-detail--card request-detail--container--0">
              <div className="requester-information request-detail--container">
                {requisitionData?.requisition?.pr_purchases_detail?.length >
                0 ? (
                  requisitionData?.requisition?.pr_purchases_detail.map(
                    (prDetail, index) => (
                      <RequestDetailItem key={index} prDetail={prDetail} />
                    ),
                  )
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          </div>
          {isUserApprover && (
            <div className="approver--actions">
              <button
                className="action-btn approve-btn"
                onClick={(e) => handleApproveDenyRequest(true)}
              >
                Approve Request
              </button>
              <button
                className="action-btn deny-btn"
                onClick={(e) => handleApproveDenyRequest(false)}
              >
                Deny Request
              </button>
            </div>
          )}
          {forTagging && (
            <div className="approver--actions">
              <button
                className="action-btn approve-btn"
                onClick={(e) => handleTagRequest(true)}
              >
                Tag Request
              </button>
            </div>
          )}
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
}

export default RequestDetail;
